import type { IGroupChatDocument } from '@common/types/api/r2-group-chat-types';
import type { IOutlookHeaderReadResult, IOutlookSelectionSnapshot, OutlookHost } from './office-hosts/outlook-host';
import { IOutlookConversationIdentity, parseOutlookMessageId, resolveOutlookConversationIdentity } from './outlook-message-headers';

export const OUTLOOK_GROUP_ID_MAX_LENGTH = 255;
export const OUTLOOK_GROUP_DIGEST_PREFIX = 'outlook:message-id:sha256:v1:';

export interface IOutlookGroupResolution
{
    readonly snapshot: IOutlookSelectionSnapshot;
    readonly mode: IOutlookConversationIdentity['mode'];
    readonly document: Readonly<IGroupChatDocument> | null;
    readonly anchorId: string | null;
    readonly messageId: string | null;
    readonly reason: IOutlookConversationIdentity['reason'] | 'stale' | 'encoding-failed';
    readonly headerStatus: IOutlookHeaderReadResult['status'];
}

export async function encodeOutlookGroupId(messageId: string): Promise<string | null>
{
    const canonicalId = parseOutlookMessageId(messageId);
    if (!canonicalId)
    {
        return null;
    }
    if (canonicalId.length <= OUTLOOK_GROUP_ID_MAX_LENGTH)
    {
        return canonicalId;
    }

    try
    {
        const data = new TextEncoder().encode(canonicalId);
        const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
        if (digest.byteLength !== 32)
        {
            return null;
        }
        const hex = Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${ OUTLOOK_GROUP_DIGEST_PREFIX }${ hex }`;
    }
    catch
    {
        return null;
    }
}

export async function resolveOutlookGroupDocument(
    host: OutlookHost,
    snapshot: IOutlookSelectionSnapshot = host.captureSelection(),
): Promise<IOutlookGroupResolution>
{
    const headers = await host.readInternetHeadersAsync(snapshot);
    const identity = resolveOutlookConversationIdentity(snapshot.internetMessageId, headers.status === 'success' ? headers.headers : null);
    const result = { ...identity, snapshot, headerStatus: headers.status };
    if (headers.status === 'stale' || !host.isSelectionCurrent(snapshot))
    {
        return { ...result, mode: 'unavailable', anchorId: null, document: null, reason: 'stale' };
    }
    if (!identity.anchorId)
    {
        return { ...result, document: null };
    }

    const id = await encodeOutlookGroupId(identity.anchorId);
    if (!host.isSelectionCurrent(snapshot))
    {
        return { ...result, mode: 'unavailable', anchorId: null, document: null, reason: 'stale' };
    }
    if (!id)
    {
        return { ...result, mode: 'unavailable', document: null, reason: 'encoding-failed' };
    }
    return { ...result, document: { vendor_id: 'microsoft', type: 'mail', id } };
}
